import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Download,
  RefreshCw,
  Lock,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Bell,
  X,
  Check,
  Send,
  TrendingUp,
  Filter,
  Info,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../../common/context/AuthContext';
import PermissionDenied from '../../common/components/PermissionDenied';
import { API_URL } from '../../api/config';
import SessionTimerIndicator from '../settings/settingModal/SessionTimerIndicator';


const MonthlyGSTReport = () => {
  const { userToken: token } = useAuth();
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlyData, setMonthlyData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderConfig, setReminderConfig] = useState({
    whatsappNumber: '',
    reminderDate: 10,
    reminderTime: '09:00',
    enabled: false
  });
  const [savedReminder, setSavedReminder] = useState(null);
  const [isSavingReminder, setIsSavingReminder] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);



  const availableYears = Array.from(
    { length: 5 }, 
    (_, i) => new Date().getFullYear() - i
  );

  useEffect(() => {
    if (token) {
      checkSubscriptionStatus();
    }
  }, [token]);

  useEffect(() => {
    if (token && subscriptionStatus && subscriptionStatus.hasAccess) {
      fetchMonthlyData();
      fetchReminderPreferences();
    }
  }, [selectedYear, token, subscriptionStatus]);

  const checkSubscriptionStatus = async () => {
    try {
      setCheckingSubscription(true);
      const response = await fetch(`${API_URL}/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch subscription status');
      }
      
      const data = await response.json();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error checking subscription status:', error);
      setError(error.message);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const handleSubscribe = () => {
    navigate('/subscription');
  };

  const fetchReminderPreferences = async () => {
    try {
      const response = await fetch(`${API_URL}/gst-reminders/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setSavedReminder(data.data);
          setReminderConfig({
            whatsappNumber: data.data.whatsappNumber,
            reminderDate: data.data.reminderDate,
            reminderTime: data.data.reminderTime,
            enabled: data.data.enabled
          });
        }
      }
    } catch (error) {
      console.error('Error fetching reminder preferences:', error);
    }
  };

  const handleSaveReminder = async () => {
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(reminderConfig.whatsappNumber)) {
      alert('Please enter a valid 10-digit Indian mobile number');
      return;
    }

    setIsSavingReminder(true);
    try {
      const response = await fetch(`${API_URL}/gst-reminders/preferences`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reminderConfig)
      });

      const data = await response.json();

      if (data.success) {
        setSavedReminder(data.data);
        alert('✅ GST Reminder saved successfully! You will receive monthly reports on WhatsApp.');
        setShowReminderModal(false);
      } else {
        alert('Failed to save reminder: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving reminder:', error);
      alert('Error saving reminder. Please try again.');
    } finally {
      setIsSavingReminder(false);
    }
  };

  const handleSendTestReminder = async () => {
    if (!savedReminder) {
      alert('Please save your reminder preferences first');
      return;
    }

    setIsSendingTest(true);
    try {
      const response = await fetch(`${API_URL}/gst-reminders/test-send`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Test reminder sent! Check your WhatsApp (or console logs in development mode).');
      } else {
        alert('Failed to send test reminder: ' + data.message);
      }
    } catch (error) {
      console.error('Error sending test reminder:', error);
      alert('Error sending test reminder. Please try again.');
    } finally {
      setIsSendingTest(false);
    }
  };

const fetchMonthlyData = async () => {
  if (!token) {
    setError('Authentication token is missing');
    return;
  }

  setIsLoading(true);
  setError(null);
  
  try {
    const url = `${API_URL}/reports/gst-yearly?year=${selectedYear}`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Access denied: contact admin for permissions');
      }
      throw new Error(`Failed to fetch GST report: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success && Array.isArray(result.monthlyData)) {
      setMonthlyData(result.monthlyData);
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error fetching monthly data:', error);
    setError(error.message);
  } finally {
    setIsLoading(false);
  }
};

  const calculateYearlyTotals = () => {
    return monthlyData.reduce((acc, month) => ({
      totalSales: acc.totalSales + month.totalSales,
      subtotal: acc.subtotal + month.subtotal,
      cgst: acc.cgst + month.cgst,
      sgst: acc.sgst + month.sgst,
      totalGST: acc.totalGST + month.totalGST,
      fivePercentOfSales: acc.fivePercentOfSales + month.fivePercentOfSales,
      billCount: acc.billCount + month.billCount,
      itemsSold: acc.itemsSold + month.itemsSold
    }), {
      totalSales: 0,
      subtotal: 0,
      cgst: 0,
      sgst: 0,
      totalGST: 0,
      fivePercentOfSales: 0,
      billCount: 0,
      itemsSold: 0
    });
  };

  const yearlyTotals = calculateYearlyTotals();

  const handlePayGSTOnPortal = () => {
    window.open('https://www.gst.gov.in/', '_blank', 'noopener,noreferrer');
  };

  const downloadGSTReport = (monthData) => {
    const reportContent = `
GST Report - ${monthData.month} ${selectedYear}
${'='.repeat(50)}

Sales Summary:
- Total Sales (with GST): ₹${monthData.totalSales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
- Subtotal (without GST): ₹${monthData.subtotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}

GST Breakdown:
- CGST (2.5%): ₹${monthData.cgst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
- SGST (2.5%): ₹${monthData.sgst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
- Total GST (5%): ₹${monthData.totalGST.toLocaleString('en-IN', { maximumFractionDigits: 2 })}

Additional Information:
- 5% of Total Sales: ₹${monthData.fivePercentOfSales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
- Total Bills: ${monthData.billCount}
- Items Sold: ${monthData.itemsSold}

${'='.repeat(50)}
Generated on: ${new Date().toLocaleString('en-IN')}
    `.trim();

    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `GST_Report_${monthData.month}_${selectedYear}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadYearlyReport = () => {
    const reportContent = `
GST Report - Full Year ${selectedYear}
${'='.repeat(50)}

Yearly Summary:
- Total Sales (with GST): ₹${yearlyTotals.totalSales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
- Subtotal (without GST): ₹${yearlyTotals.subtotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
- Total CGST: ₹${yearlyTotals.cgst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
- Total SGST: ₹${yearlyTotals.sgst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
- Total GST Collected: ₹${yearlyTotals.totalGST.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
- 5% of Total Sales: ₹${yearlyTotals.fivePercentOfSales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
- Total Bills: ${yearlyTotals.billCount}
- Total Items Sold: ${yearlyTotals.itemsSold}

${'='.repeat(50)}

Monthly Breakdown:
${monthlyData.map(month => `
${month.month}:
  Total Sales: ₹${month.totalSales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
  GST Collected: ₹${month.totalGST.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
  Bills: ${month.billCount}
`).join('')}

${'='.repeat(50)}
Generated on: ${new Date().toLocaleString('en-IN')}
    `.trim();

    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `GST_Report_Year_${selectedYear}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const calculatePercentageChange = () => {
    if (monthlyData.length < 2) return 0;
    const currentMonth = new Date().getMonth();
    const lastMonth = currentMonth - 1 < 0 ? 11 : currentMonth - 1;
    
    const currentData = monthlyData[currentMonth]?.totalGST || 0;
    const lastData = monthlyData[lastMonth]?.totalGST || 0;
    
    if (lastData === 0) return 0;
    return (((currentData - lastData) / lastData) * 100).toFixed(1);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex justify-center items-center p-4">
        <div className="max-w-md w-full bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 text-center">
          <div className="text-red-500 mb-4 text-xl font-black">Authentication Required</div>
          <p className="text-slate-600 text-sm font-bold mb-4">Please log in to view the GST report.</p>
        </div>
      </div>
    );
  }

  if (checkingSubscription) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex justify-center items-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0047FF] mx-auto"></div>
          <p className="mt-4 text-slate-600 text-sm font-bold">Checking subscription status...</p>
        </div>
      </div>
    );
  }

  if (!subscriptionStatus || !subscriptionStatus.hasAccess) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex justify-center items-center p-4">
        <div className="max-w-md w-full bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 text-center">
          <div className="bg-blue-50 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Lock className="text-[#0047FF]" size={32} />
          </div>
          <h2 className="text-2xl font-black text-slate-900 mb-4 tracking-tight">Premium Feature</h2>
          <div className="bg-blue-50 p-4 rounded-2xl mb-6">
            <p className="text-slate-700 text-sm font-bold">
              Monthly GST Report is a premium feature. Subscribe now or start your free trial to access detailed monthly analytics and GST reporting.
            </p>
          </div>
          <div className="space-y-3">
            {subscriptionStatus?.canStartTrial && (
              <p className="text-sm text-emerald-600 font-black">
                ✓ Free trial available - No credit card required
              </p>
            )}
            <button 
              onClick={handleSubscribe}
              className="w-full px-6 py-4 bg-[#0047FF] text-white font-black rounded-[2rem] shadow-xl shadow-blue-100 hover:bg-[#0038CC] transition-all"
            >
              {subscriptionStatus?.canStartTrial ? 'Start Free Trial' : 'Subscribe Now'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <PermissionDenied
        variant="card"
        title="Error Loading GST Report"
        message={error}
        onRetry={fetchMonthlyData}
        onGoHome={() => navigate('/')}
        isRetrying={isLoading}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8">
                <SessionTimerIndicator module="expenseReport" />
    <div className="min-h-screen bg-[#F8FAFC]">
      {showReminderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[2.5rem] shadow-xl max-w-md w-full p-8 max-h-[90vh] overflow-y-auto border border-slate-100">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-black text-slate-900 flex items-center gap-2 tracking-tight">
                <Bell className="text-emerald-600" />
                Setup WhatsApp Reminder
              </h2>
              <button onClick={() => setShowReminderModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
                <p className="text-sm text-blue-800 font-bold">
                  Get your monthly GST report automatically delivered to your WhatsApp every month!
                </p>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
                  WhatsApp Number (India) *
                </label>
                <input
                  type="tel"
                  placeholder="9876543210"
                  maxLength="10"
                  value={reminderConfig.whatsappNumber}
                  onChange={(e) => setReminderConfig({...reminderConfig, whatsappNumber: e.target.value.replace(/\D/g, '')})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm font-bold"
                />
                <p className="text-xs text-slate-400 mt-2 font-bold">Enter 10-digit number without country code</p>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
                  Reminder Day (of each month) *
                </label>
                <select
                  value={reminderConfig.reminderDate}
                  onChange={(e) => setReminderConfig({...reminderConfig, reminderDate: parseInt(e.target.value)})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm font-bold"
                >
                  {Array.from({length: 28}, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-2 font-bold">Day of the month to receive reminder</p>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
                  Reminder Time *
                </label>
                <input
                  type="time"
                  value={reminderConfig.reminderTime}
                  onChange={(e) => setReminderConfig({...reminderConfig, reminderTime: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm font-bold"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enableReminder"
                  checked={reminderConfig.enabled}
                  onChange={(e) => setReminderConfig({...reminderConfig, enabled: e.target.checked})}
                  className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                />
                <label htmlFor="enableReminder" className="text-sm font-black text-slate-700">
                  Enable monthly reminders
                </label>
              </div>

              {savedReminder && (
                <button
                  onClick={handleSendTestReminder}
                  disabled={isSendingTest}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 font-black text-sm"
                >
                  <Send size={16} className={isSendingTest ? 'animate-pulse' : ''} />
                  {isSendingTest ? 'Sending...' : 'Send Test Reminder Now'}
                </button>
              )}

              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
                <p className="text-xs text-amber-800 font-bold">
                  <strong>Note:</strong> You'll receive a summary of the previous month's GST report on the selected date and time. 
                  Currently in test mode - messages will be logged to the console.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowReminderModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-black text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveReminder}
                  disabled={isSavingReminder}
                  className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 font-black text-sm"
                >
                  <Check size={18} />
                  {isSavingReminder ? 'Saving...' : 'Save Reminder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="p-8 space-y-8 animate-in fade-in duration-500 pb-20">
        <div className="flex flex-col gap-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black text-slate-900 tracking-tight">Year {selectedYear}</h1>
              <p className="text-sm font-bold text-slate-400 mt-1">Annual GST & sales summary</p>
            </div>
            <div className="flex items-center gap-3">
              {savedReminder?.enabled && (
                <div className="bg-emerald-50 text-emerald-600 px-4 py-2 rounded-xl text-xs font-black flex items-center gap-2">
                  <Bell size={14} />
                  Reminders Active
                </div>
              )}
              <button
                onClick={() => setShowReminderModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-black text-xs shadow-sm"
              >
                <Bell size={16} />
                {savedReminder?.enabled ? 'Update' : 'Setup'} Reminder
              </button>
              {subscriptionStatus?.status === 'trial' && (
                <div className="bg-emerald-50 text-emerald-600 px-4 py-2 rounded-xl text-xs font-black">
                  Trial: {subscriptionStatus.daysRemaining} days left
                </div>
              )}
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#0047FF] focus:border-transparent text-sm font-bold shadow-sm"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <button
                onClick={fetchMonthlyData}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors disabled:opacity-50 font-black text-xs shadow-sm"
              >
                <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-96">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0047FF] mx-auto"></div>
                <p className="mt-4 text-slate-600 text-sm font-bold">Loading monthly data...</p>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm flex flex-col justify-between relative overflow-hidden group">
                  <div className="flex justify-between items-start z-10">
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total GST Collected</p>
                      <div className="flex items-baseline gap-1">
                        <h2 className="text-5xl font-black text-slate-900 tracking-tighter">
                          ₹{Math.floor(yearlyTotals.totalGST).toLocaleString('en-IN')}
                          <span className="text-2xl opacity-40">.{(yearlyTotals.totalGST % 1).toFixed(2).split('.')[1]}</span>
                        </h2>
                      </div>
                      <div className="flex items-center gap-2 mt-4">
                        <span className="flex items-center gap-1 bg-emerald-50 text-emerald-500 px-2 py-0.5 rounded-lg text-[10px] font-black">
                          <TrendingUp size={12} />
                          +{calculatePercentageChange()}%
                        </span>
                        <span className="text-xs font-bold text-slate-400">vs last month</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Based on Sales</p>
                      <h4 className="text-xl font-black text-slate-900">₹{Math.floor(yearlyTotals.totalSales).toLocaleString('en-IN')}</h4>
                    </div>
                  </div>

                  <div className="absolute top-12 right-12 opacity-5 pointer-events-none transform rotate-12 transition-transform group-hover:rotate-0">
                    <FileText size={180} strokeWidth={1} />
                  </div>

                  <div className="mt-12 flex items-center gap-4 z-10">
                    <button
                      onClick={handlePayGSTOnPortal}
                      className="flex-1 bg-[#0047FF] hover:bg-[#0038CC] text-white py-5 rounded-[2rem] font-black text-sm flex items-center justify-center gap-2 shadow-xl shadow-blue-100 transition-all"
                    >
                      Pay GST on Portal
                      <ArrowRight size={18} />
                    </button>
                    <button
                      onClick={downloadYearlyReport}
                      className="w-16 h-16 bg-white border border-slate-200 rounded-[2rem] flex items-center justify-center text-slate-400 hover:bg-slate-50 transition-all"
                    >
                      <Download size={22} />
                    </button>
                  </div>

                  <div className="mt-6 flex items-center gap-2 text-slate-300">
                    <Info size={14} />
                    <p className="text-[10px] font-bold italic">Data is fetched from your real-time billing system.</p>
                  </div>
                </div>

                <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm flex flex-col">
                  <h3 className="text-lg font-black text-slate-900 tracking-tight mb-8">Tax Breakdown</h3>
                  
                  <div className="flex-1 flex flex-col items-center justify-center mb-8">
                    <div className="w-40 h-40 rounded-full border-[16px] border-[#0047FF] border-t-transparent relative flex flex-col items-center justify-center">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total</p>
                      <p className="text-3xl font-black text-slate-900">5%</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 bg-slate-50/50 rounded-2xl border border-slate-50">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#0047FF]"></div>
                        <span className="text-xs font-bold text-slate-600">CGST (2.5%)</span>
                      </div>
                      <span className="text-xs font-black text-slate-900">₹{Math.floor(yearlyTotals.cgst).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50/50 rounded-2xl border border-slate-50">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-blue-300"></div>
                        <span className="text-xs font-bold text-slate-600">SGST (2.5%)</span>
                      </div>
                      <span className="text-xs font-black text-slate-900">₹{Math.floor(yearlyTotals.sgst).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-[2rem] shadow-sm p-6 border border-slate-100">
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Yearly Total Sales</h3>
                  <p className="text-2xl font-black text-slate-900">
                    ₹{yearlyTotals.totalSales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="bg-white rounded-[2rem] shadow-sm p-6 border border-slate-100">
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Subtotal (excl. GST)</h3>
                  <p className="text-2xl font-black text-slate-900">
                    ₹{yearlyTotals.subtotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="bg-white rounded-[2rem] shadow-sm p-6 border border-slate-100">
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">5% Reference</h3>
                  <p className="text-2xl font-black text-slate-900">
                    ₹{yearlyTotals.fivePercentOfSales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="bg-white rounded-[2rem] shadow-sm p-6 border border-slate-100">
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Total Bills</h3>
                  <p className="text-2xl font-black text-slate-900">
                    {yearlyTotals.billCount.toLocaleString('en-IN')}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
                <div className="p-8 flex items-center justify-between border-b border-slate-50">
                  <h3 className="text-lg font-black text-slate-900 tracking-tight">Monthly Breakdown - {selectedYear}</h3>
                  <div className="flex items-center gap-3">
                    <button className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 shadow-sm">
                      <Filter size={18} />
                      Filter
                    </button>
                    <button 
                      onClick={downloadYearlyReport}
                      className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 shadow-sm"
                    >
                      <Download size={18} />
                      Export
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                        <th className="px-8 py-5">Month</th>
                        <th className="px-6 py-5">Bills</th>
                        <th className="px-6 py-5">Total Sales</th>
                        <th className="px-6 py-5">Subtotal</th>
                        <th className="px-6 py-5">CGST (2.5%)</th>
                        <th className="px-6 py-5">SGST (2.5%)</th>
                        <th className="px-6 py-5">Total GST</th>
                        <th className="px-8 py-5 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="text-xs font-bold text-slate-600">
                      {monthlyData.map((month) => (
                        <React.Fragment key={month.monthNumber}>
                          <tr 
                            className="border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors cursor-pointer"
                            onClick={() => setExpandedMonth(expandedMonth === month.monthNumber ? null : month.monthNumber)}
                          >
                            <td className="px-8 py-5 text-slate-900 flex items-center gap-2">
                              {month.month}
                              {expandedMonth === month.monthNumber ? (
                                <ChevronUp size={16} className="text-slate-400" />
                              ) : (
                                <ChevronDown size={16} className="text-slate-400" />
                              )}
                            </td>
                            <td className="px-6 py-5">{month.billCount}</td>
                            <td className="px-6 py-5">₹{month.totalSales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td className="px-6 py-5 text-slate-400">₹{month.subtotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td className="px-6 py-5 text-slate-400">₹{month.cgst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td className="px-6 py-5 text-slate-400">₹{month.sgst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td className="px-6 py-5 text-slate-900 font-black">₹{month.totalGST.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td className="px-8 py-5 text-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadGSTReport(month);
                                }}
                                className="px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-tight bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                              >
                                Download
                              </button>
                            </td>
                          </tr>
                          {expandedMonth === month.monthNumber && (
                            <tr>
                              <td colSpan="8" className="px-8 py-6 bg-slate-50/50">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="bg-white p-5 rounded-2xl border border-slate-100">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Items Sold</p>
                                    <p className="text-2xl font-black text-slate-900">{month.itemsSold.toLocaleString('en-IN')}</p>
                                  </div>
                                  <div className="bg-yellow-50 p-5 rounded-2xl border border-yellow-100">
                                    <p className="text-[10px] font-black text-yellow-800 uppercase tracking-widest mb-2">5% Reference</p>
                                    <p className="text-2xl font-black text-yellow-900">₹{month.fivePercentOfSales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
                                    <p className="text-[9px] text-yellow-700 mt-2 font-bold">Business analysis metric</p>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadGSTReport(month);
                                    }}
                                    className="flex items-center justify-center gap-2 px-4 py-3 bg-[#0047FF] text-white rounded-xl hover:bg-[#0038CC] transition-colors font-black text-sm"
                                  >
                                    <Download size={18} />
                                    Download Report
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePayGSTOnPortal();
                                    }}
                                    className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-black text-sm"
                                  >
                                    <ExternalLink size={18} />
                                    Pay on Portal
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-[2rem] p-6">
                <h3 className="text-lg font-black text-amber-900 mb-3 tracking-tight">Important Notes:</h3>
                <ul className="list-disc list-inside space-y-2 text-sm text-amber-800 font-bold">
                  <li>GST calculations are based on your actual bill data with 5% GST (2.5% CGST + 2.5% SGST)</li>
                  <li>The "5% of Total Sales" is a reference metric separate from GST calculations</li>
                  <li>Always verify GST amounts before filing returns</li>
                  <li>Monthly reports can be downloaded for your accounting records</li>
                  <li>Data is fetched in real-time from your restaurant's billing system</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

export default MonthlyGSTReport;